<script>
    import ibTableBasic from './ib-table-basic.vue';
    import ibTableCol from './ib-table-col.vue';

    const controlWidths = {
        check: '40px',
        toggle: '64px'
    };

    /**
     * Extension of [ib-table-basic](#/components/table/ib-table-basic) with selectable rows.
     *
     * Note: Generally, the `emit` property of [ib-table-basic](#/components/table/ib-table-basic) should be specified
     * with this component. This allows selected rows to be specified or identified in a way that is not dependent on
     * issues like pagination or changes to table data.
     */
    export default {
        name: 'ib-table-select',

        mixins: [
            ibTableBasic
        ],

        props: {
            /**
             * DOM `name` attribute; used as property in enclosing `ib-form`.
             */
            name: String,
            /**
             * An array of currently selected table rows by id.
             */
            value: [String,Number,Array],
            /**
             * If `true`, allow multiple row selection.
             */
            multiple: Boolean,
            /**
             * If `true`, disable selection changes on all rows. If `Array` or `Set`, disable changes on rows whose ids
             * are in the array or set.
             */
            disabled: [Boolean,Array,Object],
            /**
             * If `true`, the form entry state will be invalid unless a row is selected.
             */
            required: Boolean,
            /**
             * Optional selection control: `check` and `toggle` are supported.
             */
            control: String,
            /**
             * Selection control position. `start` is supported; default is `end`.
             */
            controlPosition: String,
            /**
             * Click action. `none` is supported; `end` is supported to open end slot; default is `select` to select
             * current row.
             */
            click: {
                type: String,
                default: 'select'
            },
            /**
             * If `true`, highlight selected rows with a selection background color.
             */
            highlight: Boolean,
            /**
             * Sets an error message for the table.
             */
            error: String,
            errorMissing: String
        },

        data() {
            return {
                selected: []
            };
        },

        computed: {
            selectAll: {
                get() {
                    return this.selectedAll;
                },
                set(value) {
                    const index = this.index;
                    const invalid = this.selected.filter(id => !index.some(row => row.id === id));
                    this.selected = value ? [
                        ...invalid,
                        ...index.map(row => row.id).filter(id => !this.getdisabled(id))
                    ] : invalid;
                    this.$emit('input', this.selected.filter(i => !this.hidden[i]));
                }
            },

            selectedAll() {
                return !this.index.some(row => !this.getdisabled(row.id) && !this.selected.includes(row.id));
            },

            lclick() {
                return this.click != 'none' || !!this.$listeners.click;
            },

            controlComponent() {
                return {
                    check: this.multiple ? 'ib-checkbox' : 'ib-radio',
                    toggle: 'ib-toggle'
                }[this.control];
            }
        },

        watch: {
            value: {
                handler(value) {
                    if (value == null) {
                        this.selected = [];
                    } else {
                        this.selected = Array.isArray(value) ? value.slice() : [value];
                    }
                },
                immediate: true
            },

            v(visible) {
                const sel = this.selected.filter((i) => visible[i]);
                if (sel.length != this.selected.length) {
                    this.$emit('input', (this.selected = sel).slice());
                }
            }
        },

        methods: {
            renderBefore(h) {
                return this.control ? [] : [
                    h('ib-form-entry', {
                        'attrs': { 'name': this.name || '_tb' + this._uid, 'value': this.selected }
                    })
                ];
            },

            renderControlCol(h, sticky) {
                return [
                    h(ibTableCol, {
                        'class': this.controlComponent ? null : '_tbsid',
                        'attrs': {
                            sticky,
                            width: controlWidths[this.control] || '8px',
                            force: true
                        }
                    }, this.control == 'check' && this.multiple ? [
                        h('ib-checkbox', {
                            'attrs': { disabled: this.disabled === true },
                            'model': {
                                value: this.selectAll,
                                callback: value => this.selectAll = value,
                                expression: 'selectAll'
                            }
                        })] : void 0
                    )];
            },

            renderHeadBefore(h) {
                return !this.controlComponent || this.controlPosition === 'start' ? this.renderControlCol(h, 'start') : [];
            },

            renderHeadAfter(h) {
                return this.controlComponent && this.controlPosition !== 'start' ? this.renderControlCol(h, 'end') : [];
            },

            renderControl(h, id) {
                return [h('td', {
                    }, [
                        h(this.controlComponent, {
                            'ref': 'sel' + id,
                            'attrs': {
                                name: this.name || '_tb' + this._uid,
                                val: id,
                                required: this.required,
                                disabled: this.getdisabled(id),
                                error: this.error,
                                errorMissing: this.errorMissing
                            },
                            'model': {
                                value: this.selected,
                                callback: this.input,
                                expression: 'selected'
                            },
                            'on': {
                                click(e) {
                                    e.stopPropagation();
                                }
                            }
                        })
                    ])
                ];
            },

            renderBody(h) {
                const data = this.data;
                const body = this.renderSlot('body');
                const renderRow = this.renderRow;

                let highlight = {
                    check: this.highlight,
                    toggle: this.highlight
                }[this.control];
                if (highlight === void 0) {
                    highlight = true;
                }

                const bodyBefore = this.controlComponent
                    ? this.controlPosition === 'start'
                        ? this.renderControl
                        : () => []
                    : h => [h('td', {
                            'staticClass': '_tbsid'
                        }, [
                            h('div', { staticClass: '_tbst' })
                        ])
                    ];

                const bodyAfter = this.controlComponent && this.controlPosition !== 'start'
                    ? this.renderControl
                    : () => [];

                return h('tbody', {
                        'staticClass': '_tbb'
                    },
                    this.index.length
                        ? this._l(this.index, row => {
                            const rdata = data[row.index];
                            const id = row.id;
                            return renderRow(h, id, [
                                ...this.renderBodyStart(h),
                                ...bodyBefore(h, id),
                                body(rdata),
                                ...bodyAfter(h, id),
                                ...this.renderBodyEnd(h, id, rdata)
                            ], {
                                _act: highlight && this.selected.includes(id),
                                _da: this.getdisabled(id)
                            });
                        })
                        : this.renderEmpty(h));
            },

            input(value) {
                this.selected = value;
                if (this.multiple) {
                    this.$emit('input', value.filter(id => !this.hidden[id]));
                } else {
                    this.$emit('input', value[0]);
                }
            },

            /** @private */
            canhover(id) {
                return !this.getdisabled(id);
            },

            /** @private */
            clickhook(id) {
                // retrieve id in original type
                const row = this.getrow(id);
                if (row != null) {
                    if (!this.getdisabled(row.id)) {
                        if (this.multiple) {
                            this.input(this.selected.includes(row.id)
                                ? this.selected.filter(id => id != row.id)
                                : this.selected.concat(row.id));
                        } else {
                            this.input([row.id]);
                        }
                    }
                }
            },

            before() {
                const start = { width: 0 };
                if (!this.controlComponent || this.controlPosition === 'start') {
                    const controlWidth = controlWidths[this.control] || '8px';
                    const check = { width: controlWidth, sticky: 'start', force: 'true', align: 'center' };
                    return this.stickyStart >= 0 ? [start, check] : [check];
                } else {
                    return this.stickyStart >= 0 ? [start] : [];
                }
            },

            after() {
                const end = { width: 0 };
                if (this.controlComponent && this.controlPosition !== 'start') {
                    const controlWidth = controlWidths[this.control] || '8px';
                    const check = { width: controlWidth, sticky: 'end', force: 'true', align: 'center' };
                    return this.stickyEnd >= 0 ? [check, end] : [check];
                } else {
                    return this.stickyEnd >= 0 ? [end] : [];
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._tbw {
        &._sr {
            tbody {
                tr {
                    cursor: pointer;
                    -webkit-user-select: none;
                       -moz-user-select: none;
                        -ms-user-select: none;
                            user-select: none;
                    &:hover input + span {
                        border: 1px solid @fg-accent;
                    }
                }
            }
        }
    }
    tr._act {
        td {
            background-color: mix(@bg-accent, @bg, @table-active);
        }
        ._tbst {
            transform: scaleX(1);
        }
    }
    ._tbbtransparent tr._act {
        td {
            background-color: fade(@bg-accent, @table-active);
        }
    }
    ._tbsid {
        padding: 0 !important;
        text-align: center;
    }
    ._tbst {
        position: absolute;
        top: 0;
        height: 100%;
        border-left: 3px solid @fg-accent;
        transform: scaleX(0);
        transform-origin: left;
        transition: transform .25s;
    }
    [dir=rtl] ._tbst {
        transform-origin: right;
    }
</style>

<ib-example id="basic" name="Basic">
    <template lang="pug">
        div
            ib-table-select(:data="positions", v-model="selected", emit="currency", :disabled="['NZD','AZN','JPY']")
                ib-table-col(width="12ch", sort="currency", sorted) Currency
                ib-table-col(width="32ch", drop) Note
                ib-table-col(width="12ch", sort="amount", numeric) Amount
                template(v-slot:body="pos")
                    td(v-text="pos.currency")
                    td(v-text="pos.note")
                    td(v-num.fixed="pos.amount")

            pre(id="value", class="insetx-16", v-text="JSON.stringify(selected)")
    </template>
    <script>
        {
            data() {
                return {
                    positions: [
                        { currency: 'USD', amount: 20, note: 'Twenty US dollars' },
                        { currency: 'GBP', amount: 35, note: 'Thirty-five British pounds' },
                        { currency: 'NZD', amount: 12, note: 'Twelve New Zealand dollars' },
                        { currency: 'EUR', amount: 600, note: 'Six hundred euros' },
                        { currency: 'AZN', amount: 40000, note: 'Forty thousand Azerbaijan Manat' },
                        { currency: 'JPY', amount: 1000, note: 'One thousand Japanese Yen' },
                        { currency: 'AED', amount: 7500, note: 'Seven thousand five hundred UAE Dirham' },
                        { currency: 'CNY', amount: 12000, note: 'Twelve thousand Yuan Renminbi' },
                        { currency: 'INR', amount: 400, note: 'Four hundred Indian Rupees' }
                    ],
                    selected: 'USD'
                };
            }
        }
    </script>
</ib-example>

<ib-example id="multiple" name="Multiple">
    <template lang="pug">
        div
            ib-table-select(:data="positions", v-model="selected", emit="currency", multiple)
                ib-table-col(width="12ch", sort="currency", sorted) Currency
                ib-table-col(width="32ch", drop) Note
                ib-table-col(width="12ch", sort="amount", numeric) Amount
                template(v-slot:body="pos")
                    td(v-text="pos.currency")
                    td(v-text="pos.note")
                    td(v-num.fixed="pos.amount")

            pre(id="value", class="insetx-16", v-text="JSON.stringify(selected)")
    </template>
    <script>
        {
            data() {
                return {
                    positions: [
                        { currency: 'USD', amount: 20, note: 'Twenty US dollars' },
                        { currency: 'GBP', amount: 35, note: 'Thirty-five British pounds' },
                        { currency: 'NZD', amount: 12, note: 'Twelve New Zealand dollars' },
                        { currency: 'EUR', amount: 600, note: 'Six hundred euros' },
                        { currency: 'AZN', amount: 40000, note: 'Forty thousand Azerbaijan Manat' },
                        { currency: 'JPY', amount: 1000, note: 'One thousand Japanese Yen' },
                        { currency: 'AED', amount: 7500, note: 'Seven thousand five hundred UAE Dirham' },
                        { currency: 'CNY', amount: 12000, note: 'Twelve thousand Yuan Renminbi' },
                        { currency: 'INR', amount: 400, note: 'Four hundred Indian Rupees' }
                    ],
                    selected: ['JPY','NZD','USD']
                };
            }
        }
    </script>
</ib-example>

<ib-example id="check" name="Checkbox">
    <template lang="pug">
        div
            ib-table-select(:data="positions", v-model="selected", emit="currency", multiple, control="check", controlPosition="start", :disabled="['NZD','AZN','JPY']")
                ib-table-col(width="12ch", sort="currency", sorted) Currency
                ib-table-col(width="32ch", drop) Note
                ib-table-col(width="12ch", sort="amount", numeric) Amount
                template(v-slot:body="pos")
                    td(v-text="pos.currency")
                    td(v-text="pos.note")
                    td(v-num.fixed="pos.amount")

            pre(id="value", class="insetx-16", v-text="JSON.stringify(selected)")
    </template>
    <script>
        {
            data() {
                return {
                    positions: [
                        { currency: 'USD', amount: 20, note: 'Twenty US dollars' },
                        { currency: 'GBP', amount: 35, note: 'Thirty-five British pounds' },
                        { currency: 'NZD', amount: 12, note: 'Twelve New Zealand dollars' },
                        { currency: 'EUR', amount: 600, note: 'Six hundred euros' },
                        { currency: 'AZN', amount: 40000, note: 'Forty thousand Azerbaijan Manat' },
                        { currency: 'JPY', amount: 1000, note: 'One thousand Japanese Yen' },
                        { currency: 'AED', amount: 7500, note: 'Seven thousand five hundred UAE Dirham' },
                        { currency: 'CNY', amount: 12000, note: 'Twelve thousand Yuan Renminbi' },
                        { currency: 'INR', amount: 400, note: 'Four hundred Indian Rupees' }
                    ],
                    selected: ['Unknown','USD']
                };
            }
        }
    </script>
</ib-example>

<ib-example id="radio" name="Radio Button">
    <template lang="pug">
        div
            ib-table-select(:data="positions", v-model="selected", emit="currency", control="check", controlPosition="start", :disabled="['NZD','AZN','JPY']")
                ib-table-col(width="12ch", sort="currency", sorted) Currency
                ib-table-col(width="32ch", drop) Note
                ib-table-col(width="12ch", sort="amount", numeric) Amount
                template(v-slot:body="pos")
                    td(v-text="pos.currency")
                    td(v-text="pos.note")
                    td(v-num.fixed="pos.amount")

            pre(id="value", class="insetx-16", v-text="JSON.stringify(selected)")
    </template>
    <script>
        {
            data() {
                return {
                    positions: [
                        { currency: 'USD', amount: 20, note: 'Twenty US dollars' },
                        { currency: 'GBP', amount: 35, note: 'Thirty-five British pounds' },
                        { currency: 'NZD', amount: 12, note: 'Twelve New Zealand dollars' },
                        { currency: 'EUR', amount: 600, note: 'Six hundred euros' },
                        { currency: 'AZN', amount: 40000, note: 'Forty thousand Azerbaijan Manat' },
                        { currency: 'JPY', amount: 1000, note: 'One thousand Japanese Yen' },
                        { currency: 'AED', amount: 7500, note: 'Seven thousand five hundred UAE Dirham' },
                        { currency: 'CNY', amount: 12000, note: 'Twelve thousand Yuan Renminbi' },
                        { currency: 'INR', amount: 400, note: 'Four hundred Indian Rupees' }
                    ],
                    selected: 'USD'
                };
            }
        }
    </script>
</ib-example>
